import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { FileDown, Mail, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileName = (order) => `PO_${(order.po_number || order.order_name || order.order_id).replace(/[^A-Za-z0-9_\- ]/g, '').trim().replace(/ /g, '_')}.pdf`;

const fetchPdf = async (order) => {
  const res = await axios.get(`${API}/orders/${order.order_id}/pdf`, { withCredentials: true, responseType: 'blob' });
  return new File([res.data], fileName(order), { type: 'application/pdf' });
};

const EmailDialog = ({ order, open, onOpenChange, onSent }) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setMessage('');
    axios.get(`${API}/orders/${order.order_id}/email-info`, { withCredentials: true })
      .then((r) => { setTo(r.data.to); setSubject(r.data.subject); });
  }, [open, order.order_id]);

  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/orders/${order.order_id}/email`, { to, subject, message }, { withCredentials: true });
      toast.success(r.data.message);
      onOpenChange(false);
      onSent?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to email order'); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="email-order-dialog">
        <DialogHeader>
          <DialogTitle>Email purchase order to supplier</DialogTitle>
          <DialogDescription>{order.supplier_name} — the order is sent as a table with the PDF attached, from a no-reply address. The order will be marked Submitted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="email-to">To</Label>
            <Input id="email-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="orders@supplier.co.za, rep@supplier.co.za" data-testid="email-order-to" />
            {!to && <p className="text-xs text-amber-700">No email saved for this supplier. Add one in Suppliers to fill this in automatically.</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-subject">Subject</Label>
            <Input id="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-order-subject" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-message">Message (optional)</Label>
            <Textarea id="email-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Please deliver before 10am on Tuesday." data-testid="email-order-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending || !to.trim()} data-testid="email-order-send-btn">
            <Mail className="w-4 h-4 mr-2" />{sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OrderShareActions = ({ order, onSent }) => {
  const [emailOpen, setEmailOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const canShareFiles = typeof navigator !== 'undefined' && !!navigator.canShare
    && navigator.canShare({ files: [new File([''], 'x.pdf', { type: 'application/pdf' })] });

  const download = async () => {
    setBusy('pdf');
    try {
      const file = await fetchPdf(order);
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url; a.download = file.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error('Failed to create PDF'); }
    finally { setBusy(''); }
  };

  const share = async () => {
    setBusy('share');
    try {
      const file = await fetchPdf(order);
      await navigator.share({ files: [file], title: order.order_name || 'Order' });
    } catch (e) { if (e?.name !== 'AbortError') toast.error('Sharing failed'); }
    finally { setBusy(''); }
  };

  return (
    <>
      <Button variant="outline" onClick={download} disabled={busy === 'pdf'} data-testid="order-pdf-btn">
        <FileDown className="w-4 h-4 mr-2" />{busy === 'pdf' ? 'Creating...' : 'PDF'}
      </Button>
      {canShareFiles && (
        <Button variant="outline" onClick={share} disabled={busy === 'share'} data-testid="order-share-btn">
          <Share2 className="w-4 h-4 mr-2" />Share
        </Button>
      )}
      <Button variant="outline" onClick={() => setEmailOpen(true)} data-testid="order-email-btn">
        <Mail className="w-4 h-4 mr-2" />Email supplier
      </Button>
      <EmailDialog order={order} open={emailOpen} onOpenChange={setEmailOpen} onSent={onSent} />
    </>
  );
};
